import { useState, useEffect, useRef } from 'react';
import API, { formatError } from '../api/client';
import { Save, Settings, User, Upload, CheckCircle, AlertCircle, Loader, Database, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function PasswordSection() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [show, setShow] = useState({ current: false, new: false, confirm: false });

  const handleChange = async () => {
    setError(''); setSuccess('');
    if (form.new_password !== form.confirm_password) {
      setError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }
    if (form.new_password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    setSaving(true);
    try {
      await API.put('/settings/password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setSuccess('Mot de passe modifié avec succès.');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const PasswordInput = ({ field, label, showKey }) => (
    <div className="flex flex-col space-y-1.5">
      <label className="text-sm font-medium text-stone-700">{label}</label>
      <div className="relative">
        <input
          type={show[showKey] ? 'text' : 'password'}
          value={form[field]}
          onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
          data-testid={`password-${field}`}
          className="w-full px-3 py-2.5 pr-10 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={() => setShow(s => ({ ...s, [showKey]: !s[showKey] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
        >
          {show[showKey] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
          <Lock size={20} className="text-amber-600" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-stone-800">Changer le mot de passe</h3>
          <p className="text-xs text-stone-500 mt-0.5">Mettez à jour votre mot de passe de connexion</p>
        </div>
      </div>

      {error && <div data-testid="password-error" className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>}
      {success && <div data-testid="password-success" className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-lg">{success}</div>}

      <div className="space-y-4">
        <PasswordInput field="current_password" label="Mot de passe actuel" showKey="current" />
        <div className="grid grid-cols-2 gap-4">
          <PasswordInput field="new_password" label="Nouveau mot de passe" showKey="new" />
          <PasswordInput field="confirm_password" label="Confirmer le nouveau mot de passe" showKey="confirm" />
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          data-testid="change-password-button"
          onClick={handleChange}
          disabled={saving || !form.current_password || !form.new_password}
          className="flex items-center gap-2 px-6 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Lock size={15} />
          {saving ? 'Modification...' : 'Modifier le mot de passe'}
        </button>
      </div>
    </div>
  );
}

function ImportSection() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(null); // null | {running, progress, message, done, stats, error}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const pollStatus = async () => {
    try {
      const r = await API.get('/import/status');
      setStatus(r.data);
      if (r.data.done || !r.data.running) {
        clearInterval(pollRef.current);
      }
    } catch {}
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const startImport = async () => {
    if (!file) return;
    setUploading(true);
    setStatus({ running: true, progress: 0, message: 'Envoi du fichier...', done: false, stats: {} });
    try {
      const formData = new FormData();
      formData.append('file', file);
      await API.post('/import/openemr', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      // Start polling
      pollRef.current = setInterval(pollStatus, 2000);
    } catch (e) {
      setStatus({ running: false, done: true, error: true, message: formatError(e), progress: 0, stats: {} });
    } finally {
      setUploading(false);
    }
  };

  const startDirectMigration = async () => {
    setStatus({ running: true, progress: 5, message: 'Migration SQL directe en cours...', done: false, stats: {} });
    try {
      await API.post('/import/openemr-migrate');
      pollRef.current = setInterval(pollStatus, 1500);
    } catch (e) {
      setStatus({ running: false, done: true, error: true, message: formatError(e), progress: 0, stats: {} });
    }
  };

  // Cleanup
  useEffect(() => () => clearInterval(pollRef.current), []);

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <Database size={20} className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-stone-800">Import depuis OpenEMR</h3>
          <p className="text-xs text-stone-500 mt-0.5">Importez vos données patients, consultations, ordonnances et RDV depuis OpenEMR</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 text-sm text-blue-700">
        <p className="font-semibold mb-2">Mapping OpenEMR → MediConsult :</p>
        <ul className="space-y-1 text-xs text-blue-600">
          <li>• <b>patient_data</b> → Patients (nom, prénom, DOB, sexe, téléphone, ville)</li>
          <li>• <b>form_encounter + form_vitals</b> → Consultations (date, motif, signes vitaux)</li>
          <li>• <b>prescriptions</b> → Ordonnances avec médicaments</li>
          <li>• <b>openemr_postcalendar_events</b> → Rendez-vous</li>
          <li>• <b>lists</b> → Allergies, antécédents, médicaments actuels</li>
        </ul>
      </div>

      {/* File picker */}
      <div
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${file ? 'border-emerald-400 bg-emerald-50' : 'border-stone-300 hover:border-emerald-400 hover:bg-emerald-50/50'}`}
        data-testid="import-file-zone"
      >
        <input ref={fileRef} type="file" accept=".sql" onChange={handleFileChange} className="hidden" />
        <Upload size={24} className={`mx-auto mb-2 ${file ? 'text-emerald-600' : 'text-stone-400'}`} />
        {file ? (
          <div>
            <p className="text-sm font-medium text-emerald-700">{file.name}</p>
            <p className="text-xs text-emerald-600 mt-1">{(file.size / 1024 / 1024).toFixed(1)} MB — Cliquez pour changer</p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-stone-600">Glissez ou cliquez pour sélectionner le fichier SQL OpenEMR</p>
            <p className="text-xs text-stone-400 mt-1">Format accepté : .sql (export MySQL/MariaDB de draichan_openemr)</p>
          </div>
        )}
      </div>

      {/* Progress */}
      {status && (
        <div className={`mt-4 rounded-xl p-4 border ${status.error ? 'bg-red-50 border-red-200' : status.done && !status.error ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            {status.running && <Loader size={16} className="text-blue-600 animate-spin" />}
            {status.done && !status.error && <CheckCircle size={16} className="text-emerald-600" />}
            {status.error && <AlertCircle size={16} className="text-red-600" />}
            <p className={`text-sm font-medium ${status.error ? 'text-red-700' : status.done ? 'text-emerald-700' : 'text-stone-700'}`}>
              {status.message}
            </p>
          </div>
          {status.running && (
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
              <div
                className="h-2 bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${status.progress}%` }}
              />
            </div>
          )}
          {status.done && !status.error && status.stats && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              {Object.entries(status.stats).map(([key, val]) => (
                <div key={key} className="bg-white border border-emerald-200 rounded-lg p-2.5 text-center">
                  <p className="text-xl font-bold text-emerald-600">{val}</p>
                  <p className="text-xs text-stone-500 capitalize">{key.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-3 flex-wrap">
        <button
          data-testid="migrate-direct-button"
          onClick={startDirectMigration}
          disabled={uploading || status?.running}
          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          title="Si le fichier SQL est déjà importé sur le serveur (base openemr_import)"
        >
          {status?.running ? <Loader size={15} className="animate-spin" /> : <CheckCircle size={15} />}
          Migration directe (serveur)
        </button>
        <button
          data-testid="start-import-button"
          onClick={startImport}
          disabled={!file || uploading || status?.running}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {uploading || status?.running ? (
            <><Loader size={15} className="animate-spin" /> Import en cours...</>
          ) : (
            <><Database size={15} /> Upload + Import</>
          )}
        </button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    nom: '', specialite: 'Médecin Généraliste',
    cabinet: '', adresse_cabinet: '', telephone_cabinet: '',
    ville: '', inpe: '',
    nom_ar: '', titres_fr: '', titres_ar: '', ice: ''
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    API.get('/settings/profile').then(r => {
      const d = r.data;
      setForm({
        nom: d.nom || '',
        specialite: d.specialite || 'Médecin Généraliste',
        cabinet: d.cabinet || '',
        adresse_cabinet: d.adresse_cabinet || '',
        telephone_cabinet: d.telephone_cabinet || '',
        ville: d.ville || '',
        inpe: d.inpe || '',
        nom_ar: d.nom_ar || '',
        titres_fr: d.titres_fr || '',
        titres_ar: d.titres_ar || '',
        ice: d.ice || '',
      });
    });
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await API.put('/settings/profile', form);
      setUser(u => ({ ...u, nom: form.nom }));
      setSuccess('Profil mis à jour avec succès.');
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const F = ({ label, children }) => (
    <div className="flex flex-col space-y-1.5">
      <label className="text-sm font-medium text-stone-700">{label}</label>
      {children}
    </div>
  );

  const inp = (field, placeholder = '', type = 'text') => (
    <input type={type} placeholder={placeholder} value={form[field] || ''}
      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors" />
  );

  return (
    <div className="space-y-6 animate-fade-in-up max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-stone-900 flex items-center gap-2">
          <Settings size={22} className="text-stone-600" />
          Paramètres
        </h1>
        <p className="text-stone-500 text-sm mt-0.5">Informations du médecin pour les ordonnances</p>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <User size={24} className="text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-stone-800">{form.nom || user?.nom}</p>
            <p className="text-sm text-stone-500">{user?.email}</p>
          </div>
        </div>

        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>}
        {success && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-lg">{success}</div>}

        <div className="space-y-5">
          <div>
            <h3 className="font-heading font-semibold text-stone-700 text-xs uppercase tracking-wide mb-3">Informations personnelles</h3>
            <div className="grid grid-cols-2 gap-4">
              <F label="Nom complet">{inp('nom', 'Dr. Prénom Nom')}</F>
              <F label="Spécialité">
                <input type="text" placeholder="Médecin Généraliste" value={form.specialite}
                  onChange={e => setForm(f => ({ ...f, specialite: e.target.value }))}
                  className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
              </F>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-stone-700 text-xs uppercase tracking-wide mb-3">Cabinet médical</h3>
            <div className="grid grid-cols-2 gap-4">
              <F label="Nom du cabinet">{inp('cabinet', 'Cabinet Dr. ...')}</F>
              <F label="Téléphone">{inp('telephone_cabinet', '05XXXXXXXX', 'tel')}</F>
              <F label="Adresse" >{inp('adresse_cabinet', 'Rue, N°, Quartier')}</F>
              <F label="Ville">{inp('ville', 'Casablanca, Rabat...')}</F>
              <F label="INPE (Identifiant National)">
                <input type="text" placeholder="N° INPE" value={form.inpe}
                  onChange={e => setForm(f => ({ ...f, inpe: e.target.value }))}
                  className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
              </F>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-stone-700 text-xs uppercase tracking-wide mb-3">En-tête d'ordonnance (bilingue)</h3>
            <div className="grid grid-cols-2 gap-4">
              <F label="Nom du médecin (arabe)">
                <input type="text" dir="rtl" placeholder="الدكتور ..." value={form.nom_ar}
                  onChange={e => setForm(f => ({ ...f, nom_ar: e.target.value }))}
                  className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
              </F>
              <F label="ICE">{inp('ice', 'Identifiant Commun de l\'Entreprise')}</F>
              <F label="Diplômes / Titres (français) — une ligne par titre">
                <textarea rows={5} placeholder={"DIU en Nutrition et Maladies Métaboliques (France)\nDocteur en Médecine Générale (Rabat)\n..."} value={form.titres_fr}
                  onChange={e => setForm(f => ({ ...f, titres_fr: e.target.value }))}
                  className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
              <F label="Diplômes / Titres (arabe) — une ligne par titre">
                <textarea rows={5} dir="rtl" placeholder={"إجازة جامعية في التغذية وأمراض السكري (فرنسا)\n..."} value={form.titres_ar}
                  onChange={e => setForm(f => ({ ...f, titres_ar: e.target.value }))}
                  className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-700">
            <p className="font-medium mb-1">Informations pour les ordonnances</p>
            <p className="text-emerald-600 text-xs">Ces informations (FR &amp; arabe) apparaissent en en-tête de toutes vos ordonnances imprimées.</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            data-testid="save-settings-button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Save size={16} />
            {saving ? 'Enregistrement...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      <PasswordSection />
      <ImportSection />
    </div>
  );
}
